/**
 * useActivityRightsByType — 按活动类型查询活动权益
 *
 * ## 职责
 * 根据 `type`（活动类型字符串）从服务端拉取活动列表，找到对应条目，
 * 并结合服务器当前时间计算"活动是否可用"和"剩余秒数"。
 *
 * ## 数据来源
 * - 活动列表：`GET /api/rights/getActivityRights` → `activityInfos[]`
 * - 当前时间：`useSystemTimeStore`（与服务器时钟对齐，避免客户端时钟偏差）
 * - 时间字段：活动的 startTime / endTime 均为上海时区（CST，UTC+8）字符串
 *
 * ## 模块级单例缓存
 * `activityInfos`、`loading`、`loaded` 声明在模块作用域（非函数内），
 * 多个组件调用同一 Hook 时共享同一份数据，不会重复请求。
 * `inflightRequest` 保证并发调用时只发一次网络请求（并发去重）。
 *
 * ## 使用示例
 * ```ts
 * // 按类型使用
 * const { isAvailable, remainingSeconds, refresh } = useActivityRightsByType('free_canvas')
 *
 * // 快捷方式（使用 CURRENT_POWER_ACTIVITY_TYPE 常量）
 * const { isAvailable, remainingSeconds } = useCurrentPowerActivityRights()
 * ```
 */

import { computed, readonly, ref, unref } from "vue";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { getActivityRights, type ActivityInfo } from "@/api/rights";
import { useSystemTimeStore } from "@/store/systemTime";
import { CURRENT_POWER_ACTIVITY_TYPE } from "@/constants/activity";
import { useUserStore } from "@/store/user";
import {
  getActivity618DebugRemainingSeconds,
  isActivity618DebugEnabled
} from "@/utils/activity618Debug";

dayjs.extend(utc);
dayjs.extend(timezone);

const SHANGHAI_TZ = "Asia/Shanghai";

// 模块级单例：所有调用方共享同一份活动列表，避免重复请求
const activityInfos = ref<ActivityInfo[]>([]);
const loading = ref(false);
const loaded = ref(false);
const errorMessage = ref("");
// 并发去重：已有进行中的请求时，后续调用复用同一 Promise
let inflightRequest: Promise<void> | null = null;

/**
 * 将服务端返回的时间戳统一转为毫秒级。
 * 服务端可能返回秒级（10位）或毫秒级（13位），此处做兼容处理。
 */
const normalizeServerTimestamp = (timestamp: number) => {
  if (!timestamp) return 0;
  return timestamp < 1e12 ? timestamp * 1000 : timestamp;
};

/**
 * 将上海时区的时间字符串解析为 Unix 毫秒时间戳。
 * 活动的 startTime / endTime 字段由服务端以 CST（UTC+8）格式下发，
 * 必须用 dayjs.tz 指定时区解析，否则在非上海时区的机器上会产生偏差。
 */
const parseShanghaiTime = (timeText?: string) => {
  if (!timeText) return 0;
  const parsed = dayjs.tz(timeText, SHANGHAI_TZ);
  return parsed.isValid() ? parsed.valueOf() : 0;
};

/**
 * 惰性加载活动权益列表，内部实现了缓存与并发去重。
 * @param userId  已登录用户 ID，未登录时传 undefined
 * @param force   强制刷新，忽略已有缓存
 */
const ensureActivityRightsLoaded = async (userId?: string, force = false) => {
  // 已加载且不强制刷新时，直接复用缓存
  if (!force && loaded.value) return;
  // 已有进行中的请求时，等待其完成，不重复发起
  if (inflightRequest) return inflightRequest;

  inflightRequest = (async () => {
    loading.value = true;
    errorMessage.value = "";
    try {
      const { data } = await getActivityRights(userId);
      activityInfos.value = Array.isArray(data?.activityInfos) ? data.activityInfos : [];
      loaded.value = true;
    } catch (error) {
      activityInfos.value = [];
      errorMessage.value = error instanceof Error ? error.message : "获取活动信息失败";
      // NOTE: 请求失败时 loaded 保持 false，下次调用会重新发起请求
      loaded.value = false;
    } finally {
      loading.value = false;
      inflightRequest = null;
    }
  })();

  return inflightRequest;
};

/**
 * 按活动类型查询活动权益状态
 *
 * @param type 活动类型字符串，对应 `ActivityInfo.type`，例如 `"free_canvas"`
 *
 * @returns
 * - `activityInfo`     — 当前类型的完整活动信息（只读），未找到时为 null
 * - `isAvailable`      — 活动是否可用（`status === 1`）
 * - `status`           — 活动状态原始值（0：未开启，1：已开启，2：已结束）
 * - `remainingSeconds` — 距活动结束的剩余秒数，基于服务器时间计算；不可用时为 0
 * - `startTimestamp`   — 活动开始时间戳（ms）
 * - `endTimestamp`     — 活动结束时间戳（ms）
 * - `loading`          — 是否正在加载（只读）
 * - `errorMessage`     — 请求错误信息（只读）
 * - `refresh`          — 强制重新拉取活动列表和服务器时间
 */
export function useActivityRightsByType(type: string) {
  const systemTimeStore = useSystemTimeStore();
  const userStore = useUserStore();
  const typeValue = String(type || "").trim();
  const userId = computed(() => {
    if (!userStore.isLogin) return undefined;
    return String(userStore.userInfo.id || "");
  });

  // 服务器当前时间（毫秒），兼容服务端返回秒级或毫秒级时间戳
  const serverTimestampMs = computed(() => normalizeServerTimestamp(unref(systemTimeStore.timestamp)));

  // 从共享列表中找到当前 type 对应的活动条目
  const activityInfo = computed<ActivityInfo | null>(() => {
    if (!typeValue) return null;
    return activityInfos.value.find((item) => item.type === typeValue) ?? null;
  });

  const status = computed(() => {
    if (isActivity618DebugEnabled() && typeValue === CURRENT_POWER_ACTIVITY_TYPE) return 1;
    return activityInfo.value?.status ?? 0;
  });

  // 业务规则：status === 1 才视为"活动可用"
  const isAvailable = computed(() => status.value === 1);

  // 将上海时区的时间字符串转为毫秒时间戳，供倒计时组件直接使用
  const startTimestamp = computed(() => parseShanghaiTime(activityInfo.value?.startTime));
  const endTimestamp = computed(() => parseShanghaiTime(activityInfo.value?.endTime));

  // 用服务器时间而非客户端本地时间计算剩余秒数，防止客户端时钟偏差导致活动提前/延迟结束
  const remainingSeconds = computed(() => {
    if (!isAvailable.value) return 0;
    if (!serverTimestampMs.value) return 0;

    // 优先使用接口下发的 endTime；调试开关只负责强制开启活动态，不覆盖倒计时
    const endMs = endTimestamp.value;
    if (!endMs) {
      if (isActivity618DebugEnabled() && typeValue === CURRENT_POWER_ACTIVITY_TYPE) {
        return getActivity618DebugRemainingSeconds(serverTimestampMs.value);
      }
      return 0;
    }

    const remain = endMs - serverTimestampMs.value;
    return Math.max(0, Math.floor(remain / 1000));
  });

  /**
   * 强制刷新活动列表和服务器时间，适用于支付完成、权益变更后需要即时更新的场景。
   * 并行发起两个请求以减少等待时间。
   */
  const refresh = async () => {
    await Promise.all([systemTimeStore.getServerTime(), ensureActivityRightsLoaded(userId.value, true)]);
  };

  // 初始化时并行预取服务器时间和活动列表（有缓存时不重复请求）
  void Promise.all([systemTimeStore.getServerTime(), ensureActivityRightsLoaded(userId.value)]);

  return {
    activityInfo: readonly(activityInfo),
    isAvailable,
    status,
    loading: readonly(loading),
    errorMessage: readonly(errorMessage),
    remainingSeconds,
    startTimestamp,
    endTimestamp,
    refresh
  };
}

/**
 * 获取当前主推活动权益（使用 CURRENT_POWER_ACTIVITY_TYPE 常量）。
 * 切换活动时只需修改 `src/constants/activity.ts` 中的常量，无需改动调用方。
 */
export function useCurrentPowerActivityRights() {
  return useActivityRightsByType(CURRENT_POWER_ACTIVITY_TYPE);
}
